import React from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowUpRight,
  Download,
  CheckCircle2,
  AlertCircle,
  Lock,
} from "lucide-react";
import { BuyMeACoffeeButton } from "@/components/BuyMeACoffeeButton";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LeadCaptureModal — the site-wide gated lead-capture modal.
 *
 * Consumed by both /resources and /apps. `resource` is the caller-supplied
 * shape describing what the user just clicked:
 *   - slug:            stable machine slug (also passed to backend)
 *   - resourceLabel:   human string stored on the lead's `resource` field
 *   - title:           modal H1
 *   - body:            supporting description
 *   - icon:            lucide-react icon component
 *   - kind:            small badge above title (e.g. "PDF", "APP")
 *   - cta:             submit-button copy
 *   - source:          `source` field written to the lead
 *   - comingSoon:      switches success copy to a "you're on the waitlist"
 *                      voice and skips the auto-open of download URLs
 */
export function LeadCaptureModal({ resource, onClose }) {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (resource) {
      setName("");
      setEmail("");
      setResult(null);
      setSubmitting(false);
    }
  }, [resource]);

  const open = Boolean(resource);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!resource) return;
    if (!name.trim() || !email.trim()) {
      setResult({ ok: false, msg: "Please enter your name and email so we can deliver your resource." });
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        resource: resource.resourceLabel,
        resource_slug: resource.slug,
        source: resource.source,
      });
      setResult({
        ok: true,
        downloadReady: Boolean(data?.download_ready),
        downloadUrl: data?.download_url || null,
        downloadAssets: Array.isArray(data?.download_assets) ? data.download_assets : null,
      });
      // Auto-open only for ready-to-download resources — waitlist joins never
      // open anything (there's nothing to deliver yet).
      if (!resource.comingSoon && data?.download_ready && data?.download_url) {
        try {
          window.open(data.download_url, "_blank", "noopener,noreferrer");
        } catch {
          /* popup blocked — the manual links are still shown */
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setResult({
        ok: false,
        msg:
          typeof detail === "string"
            ? detail
            : "We couldn't process your request just now. Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!resource) return null;
  const Icon = resource.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="lead-capture-modal"
        className="border border-[#D4AF37]/30 bg-[#142339] text-white sm:max-w-lg p-0 rounded-none"
      >
        <div className="p-8 sm:p-10">
          <DialogHeader className="text-left space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                {Icon && <Icon className="w-5 h-5" />}
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">
                {resource.kind}
              </span>
            </div>
            <DialogTitle className="font-serif-display text-2xl sm:text-3xl text-white leading-tight">
              {resource.title}
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm leading-relaxed">
              {resource.body}
            </DialogDescription>
          </DialogHeader>

          {!result?.ok && (
            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <div>
                <label
                  htmlFor="lead-capture-name"
                  className="block text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-2"
                >
                  Your Name
                </label>
                <input
                  id="lead-capture-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  data-testid="lead-capture-name"
                  className="w-full bg-[#1A2B45] border border-white/10 text-white px-4 py-3 text-sm rounded-none placeholder:text-slate-500 focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none"
                />
              </div>
              <div>
                <label
                  htmlFor="lead-capture-email"
                  className="block text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="lead-capture-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="lead-capture-email"
                  className="w-full bg-[#1A2B45] border border-white/10 text-white px-4 py-3 text-sm rounded-none placeholder:text-slate-500 focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-capture-submit"
                className={`w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors ${
                  submitting ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {submitting ? "Sending…" : (
                  <>
                    <Download className="w-3.5 h-3.5" />
                    {resource.cta}
                  </>
                )}
              </button>

              {/* Human-in-the-Loop compliance micro-copy */}
              <div className="pt-3 border-t border-white/10">
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  <Lock className="inline w-3 h-3 mr-1 text-[#D4AF37]/70 align-[-1px]" />
                  Your name and email are used solely to deliver this
                  {resource.comingSoon ? " waitlist notice" : " resource"} and
                  to send a single follow-up should Dr. Byers publish an update.
                  No marketing lists, no third-party sharing. Reply
                  &quot;remove&quot; to any message to be scrubbed immediately.
                  Reviewed by a human, not a black-box bot.
                </p>
              </div>

              {result && !result.ok && (
                <div
                  data-testid="lead-capture-error"
                  className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-red-300"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{result.msg}</span>
                </div>
              )}
            </form>
          )}

          {result?.ok && (
            <div className="mt-8 space-y-5" data-testid="lead-capture-success">
              <div className="flex items-start gap-3 text-[#D4AF37]">
                <CheckCircle2 className="w-6 h-6 mt-0.5 shrink-0" />
                <div>
                  <p className="font-serif-display text-xl text-white leading-tight">
                    {resource.comingSoon && !result.downloadReady
                      ? `On the list, ${name.split(" ")[0] || "friend"}.`
                      : `You're set, ${name.split(" ")[0] || "friend"}.`}
                  </p>
                  <p className="mt-2 text-slate-300 text-sm leading-relaxed">
                    {result.downloadReady
                      ? (result.downloadAssets && result.downloadAssets.length > 1
                        ? "Your resources just opened in a new tab. Use the buttons below to grab every file — they're yours to keep. A copy of your request has been logged so Greg can follow up personally."
                        : "Your resource just opened in a new tab. If your browser blocked the pop-up, use the button below. A copy has also been logged so Greg can follow up personally.")
                      : (resource.comingSoon
                        ? "You're officially on the waitlist. We'll email you at the address above the moment this ships — no spam, no marketing list, one email, one launch notice."
                        : "This resource is being finalized right now. We've logged your request and will email your copy to the address you provided within 24 hours — most inquiries receive same-business-day delivery.")}
                  </p>
                </div>
              </div>

              {result.downloadReady && result.downloadAssets && result.downloadAssets.length > 0 && (
                <>
                  <div
                    data-testid="lead-capture-bmc-block"
                    className="border-t border-b border-[#D4AF37]/20 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left"
                  >
                    <p className="text-slate-300 text-xs sm:text-sm leading-snug max-w-xs">
                      Found this useful?{" "}
                      <span className="text-white">Buy Greg a coffee.</span>{" "}
                      Every tip fuels the next resource.
                    </p>
                    <BuyMeACoffeeButton
                      variant="solid"
                      label="Buy Me a Coffee"
                      testid="lead-capture-bmc-btn"
                      className="shrink-0"
                    />
                  </div>

                  <div className="space-y-3" data-testid="lead-capture-download-list">
                    {result.downloadAssets.map((asset, i) => (
                      <a
                        key={i}
                        href={asset.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`lead-capture-download-link-${i}`}
                        className="w-full inline-flex items-center justify-between gap-3 bg-[#D4AF37] text-[#142339] px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <Download className="w-3.5 h-3.5" />
                          {asset.label}
                        </span>
                        <ArrowUpRight className="w-4 h-4" />
                      </a>
                    ))}
                  </div>
                </>
              )}

              {result.downloadReady && (
                <GuestUpsell />
              )}

              <button
                type="button"
                onClick={onClose}
                data-testid="lead-capture-close"
                className="w-full inline-flex items-center justify-center gap-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-medium transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Shown in the modal success view when a guest just claimed a resource.
 * Nudges them to create a free account for 1-click access forever.
 */
function GuestUpsell() {
  const { isAuthed } = useAuth();
  const [authOpen, setAuthOpen] = React.useState(false);
  if (isAuthed) return null;
  return (
    <>
      <div
        data-testid="lead-capture-account-upsell"
        className="border border-[#D4AF37]/30 bg-[#0A1020] px-5 py-4 text-sm text-slate-300 leading-relaxed"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-1">
          Skip the form next time
        </div>
        <p>
          Want 1-click downloads for all current and future resources?{" "}
          <button
            type="button"
            onClick={() => setAuthOpen(true)}
            data-testid="lead-capture-create-account-cta"
            className="text-[#D4AF37] hover:text-[#FDE047] underline underline-offset-2 font-semibold"
          >
            Create a Free Account
          </button>
        </p>
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialTab="register" />
    </>
  );
}

export default LeadCaptureModal;
